/**
 * skuFitCheck — predict whether a SKU will fit in a room before placing it.
 *
 * Uses typed Room dimensions (areaSqM / area sq ft) + ontology SKU.category
 * footprint defaults to predict whether adding ONE more of this category
 * leaves enough clearance for the room to remain functional per the
 * standards the agentSuggestions detectors cite:
 *   · NBC 2016 Part 4 §4.3.6     — habitable room min dim 2.4 m
 *   · NKBA Guideline 6           — kitchen aisle ≥ 1.07 m single-cook
 *   · NKBA Guideline 17          — bedroom 0.6 m clear at foot of bed
 *   · ANSI A117.1-2017 §304      — 1.5 m clear turning radius
 *
 *  Returns a typed verdict the CatalogRail consults to bar non-fitting
 *  adds, and the AgentBus surfaces as a Why-traceable Suggestion.
 *
 *  Category footprints come from the same SKU_VISUALS dimensions table
 *  the 3D scene uses — single source of truth. The placer in B.1/B.2 will
 *  refine with real geometry; this is the pre-fit heuristic that fires
 *  client-side without a round-trip.
 */
import type { GraphNode, ProjectGraph } from "../types/ontology";
import codegen from "../../../docs/ontology/codegen.json";

/** Ontology-typed category → roomKind affinity (codegen.json#skuRoomAffinity).
 *  Designer says some categories belong in specific rooms; the agent reads
 *  this typed table to surface warnings when a placement crosses the line.
 *  Empty arrays mean "no affinity check" (lights, rugs, storage). */
interface AffinityRow { category: string; preferred: string[]; tolerated: string[]; forbidden: string[]; note: string }
const AFFINITY: ReadonlyArray<AffinityRow> = (codegen.skuRoomAffinity ?? []) as ReadonlyArray<AffinityRow>;
const AFFINITY_BY_CAT = new Map<string, AffinityRow>(AFFINITY.map((r) => [r.category, r]));

// Per-category footprint table — read FROM codegen.json#categoryFootprints
// (ontology data, not hardcoded). Every entry: footprintSqM (effective
// box including clearance) + clearanceBuffer (minimum gap on at least
// one side) + dimensions (wM × dM) + ontology anchor (south/north/east/
// west/center/overhead). CLAUDE.md §1 — no hardcoded magic numbers
// outside the ontology.
interface FootprintRow {
  category: string;
  footprintSqM: number;
  clearanceBuffer: number;
  anchor?: string;
  wM?: number;
  dM?: number;
  note?: string;
}
const FOOTPRINT_BY_CATEGORY: Readonly<Record<string, { footprintSqM: number; clearanceBuffer: number }>> = (() => {
  const arr = (codegen.categoryFootprints ?? []) as ReadonlyArray<FootprintRow>;
  const out: Record<string, { footprintSqM: number; clearanceBuffer: number }> = {};
  for (const r of arr) out[r.category] = { footprintSqM: r.footprintSqM, clearanceBuffer: r.clearanceBuffer };
  return out;
})();

// Statutory thresholds — read FROM codegen.json#statutoryThresholds. The
// table drives BOTH the circulation reserve (kind = circulationReserve*)
// AND the per-category checks (kind = categoryMinDimWithBuffer /
// categoryMinRoomArea). Adding a new statutory rule means adding a row
// to the JSON — zero code change here. CLAUDE.md §1: nothing hardcoded.
type ThresholdKind =
  | "circulationReserveLarge"
  | "circulationReserveSmall"
  | "smallRoomCutoff"
  | "categoryMinDimWithBuffer"
  | "categoryMinRoomArea";
interface StatThreshold {
  id: string;
  kind: ThresholdKind;
  valueM?: number;
  valueSqM?: number;
  citation: string;
  label: string;
  triggerCategory?: string;
  reasonTemplate?: string;
}
const STAT_ALL: ReadonlyArray<StatThreshold> = (codegen.statutoryThresholds ?? []) as ReadonlyArray<StatThreshold>;
function statByKind(kind: ThresholdKind): StatThreshold | undefined {
  return STAT_ALL.find((r) => r.kind === kind);
}
function statsForCategory(cat: string): ReadonlyArray<StatThreshold> {
  return STAT_ALL.filter((r) => r.triggerCategory === cat);
}
function fillTemplate(tpl: string, vars: Record<string, string | number>): string {
  return tpl.replace(/\{(\w+)\}/g, (_m, k: string) => String(vars[k] ?? `{${k}}`));
}

export interface FitVerdict {
  readonly ok: boolean;
  /** When ok is false — why. When true — how much headroom remains. */
  readonly reason: string;
  /** Sq-m the room has free of the existing placements + this candidate. */
  readonly remainingFreeSqM: number;
  /** Citation tag for the rule the verdict ties to. */
  readonly citation: string;
  /** Soft warning that doesn't block the add but should surface in the UI
   *  (e.g. "Sofa in Kitchen — unusual but allowed in open-plan layouts"). */
  readonly soft?: string;
}

function attrNum(n: GraphNode | undefined, key: string): number | null {
  if (!n) return null;
  const v = n.attrs[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

function attrStr(n: GraphNode | undefined, key: string): string | null {
  if (!n) return null;
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

/** Compute fit verdict for adding `candidate` SKU into `room`. */
export function wouldFit(graph: ProjectGraph, room: GraphNode, candidate: GraphNode): FitVerdict {
  const roomAreaSqM = attrNum(room, "areaSqM")
    ?? (attrNum(room, "area") ?? 0) * 0.092903;
  if (roomAreaSqM <= 0) {
    return { ok: true, reason: "Room area unknown — adding without fit check.", remainingFreeSqM: 0, citation: "no-area-data" };
  }

  // FIRST — check category↔roomKind FORBIDDEN list. Bunk bed in kitchen,
  // wardrobe in bath, etc. — statutory / hygienic blockers, never overruled.
  const earlyRoomKind = (attrStr(room, "roomKind") ?? "").toLowerCase();
  const earlyCat = attrStr(candidate, "category") ?? "Item";
  const earlyAff = AFFINITY_BY_CAT.get(earlyCat);
  if (earlyAff && earlyRoomKind && earlyAff.forbidden && earlyAff.forbidden.some((k) => earlyRoomKind.includes(k))) {
    return {
      ok: false,
      reason: `${earlyCat} cannot be placed in ${room.label} (${earlyRoomKind}).${earlyAff.note ? ` ${earlyAff.note}` : ""} Forbidden combination per ontology.`,
      remainingFreeSqM: 0,
      citation: `affinity/forbidden/${earlyCat}-${earlyRoomKind}`,
    };
  }

  const existingIds = new Set(
    graph.edges.filter((e) => e.kind === "IN_ROOM" && e.target === room.id).map((e) => e.source),
  );
  const existing = graph.nodes.filter((n) => n.kind === "SKU" && existingIds.has(n.id));

  // Sum existing footprints from typed category.
  let consumed = 0;
  for (const sku of existing) {
    const cat = attrStr(sku, "category") ?? "Item";
    consumed += (FOOTPRINT_BY_CATEGORY[cat] ?? FOOTPRINT_BY_CATEGORY["Item"]!).footprintSqM;
  }

  const cat = attrStr(candidate, "category") ?? "Item";
  const cfg = FOOTPRINT_BY_CATEGORY[cat] ?? FOOTPRINT_BY_CATEGORY["Item"]!;
  const needed = cfg.footprintSqM;

  // Circulation reserve — both value AND the small-room cutoff come from
  // the ontology table. Each row carries its own citation, so the
  // rejection event quotes the right standard automatically.
  const turnRadius = statByKind("circulationReserveLarge");
  const walkwayMin = statByKind("circulationReserveSmall");
  const smallRoom  = statByKind("smallRoomCutoff");
  if (!turnRadius?.valueM || !walkwayMin?.valueM || !smallRoom?.valueSqM) {
    throw new Error("statutoryThresholds missing circulation rows — codegen.json out of sync with skuFitCheck.ts");
  }
  const useSmallRoomBuffer = roomAreaSqM < smallRoom.valueSqM;
  const circulationReserve = useSmallRoomBuffer
    ? walkwayMin.valueM * walkwayMin.valueM
    : turnRadius.valueM * turnRadius.valueM;
  const circulationCitation = useSmallRoomBuffer ? walkwayMin.citation : turnRadius.citation;
  const totalConsumed = consumed + needed + circulationReserve;
  const remaining = roomAreaSqM - totalConsumed;

  if (remaining < 0) {
    return {
      ok: false,
      reason: `"${room.label}" has ${roomAreaSqM.toFixed(1)} m² but ${existing.length} existing placements + a ${cat} need ${(consumed + needed).toFixed(1)} m² + ${circulationReserve.toFixed(1)} m² circulation. Shortfall ${Math.abs(remaining).toFixed(1)} m².`,
      remainingFreeSqM: remaining,
      citation: circulationCitation,
    };
  }

  // Category-specific checks — driven by ontology, NOT by hardcoded
  // if/else branches. Each row whose triggerCategory matches gets
  // applied; the kind dispatches the check; reasonTemplate fills out
  // the human-readable message. Adding a Bathtub min-clearance rule
  // is a JSON edit, not a code edit.
  for (const rule of statsForCategory(cat)) {
    if (rule.kind === "categoryMinDimWithBuffer" && rule.valueM !== undefined) {
      const minDim = attrNum(room, "minDimM");
      const required = rule.valueM + cfg.clearanceBuffer;
      if (minDim !== null && minDim < required) {
        return {
          ok: false,
          reason: rule.reasonTemplate
            ? fillTemplate(rule.reasonTemplate, {
                value: rule.valueM.toFixed(1),
                buffer: cfg.clearanceBuffer.toFixed(2),
                room: room.label,
                actual: minDim.toFixed(2),
              })
            : `${cat} requires ≥ ${required.toFixed(2)} m min-dim per ${rule.citation}.`,
          remainingFreeSqM: remaining,
          citation: rule.citation,
        };
      }
    } else if (rule.kind === "categoryMinRoomArea" && rule.valueSqM !== undefined) {
      if (roomAreaSqM < rule.valueSqM) {
        return {
          ok: false,
          reason: rule.reasonTemplate
            ? fillTemplate(rule.reasonTemplate, {
                value: rule.valueSqM.toFixed(0),
                room: room.label,
                actual: roomAreaSqM.toFixed(1),
              })
            : `${cat} requires room ≥ ${rule.valueSqM.toFixed(1)} m² per ${rule.citation}.`,
          remainingFreeSqM: remaining,
          citation: rule.citation,
        };
      }
    }
  }

  // Category-vs-roomKind affinity — interior-designer's eye. Sofa in Kitchen
  // is unusual but not impossible (open-plan / breakfast nook). The ontology
  // table drives the verdict — designer can override this without code change
  // by editing skuRoomAffinity in codegen.json.
  const roomKind = (attrStr(room, "roomKind") ?? "").toLowerCase();
  const affinity = AFFINITY_BY_CAT.get(cat);
  let soft: string | undefined;
  if (affinity && roomKind && affinity.preferred.length > 0) {
    const preferredOk = affinity.preferred.some((k) => roomKind.includes(k));
    const toleratedOk = affinity.tolerated.some((k) => roomKind.includes(k));
    if (!preferredOk && !toleratedOk) {
      soft = `${cat} in ${room.label} (${roomKind}) is unusual — typically belongs in ${affinity.preferred.join(" / ")}.${affinity.note ? ` ${affinity.note}` : ""} Allowed because some layouts (open-plan, breakfast nook) accept it.`;
    } else if (!preferredOk && toleratedOk) {
      soft = `${cat} in ${roomKind} is tolerated (preferred: ${affinity.preferred.join(" / ")}).${affinity.note ? ` ${affinity.note}` : ""}`;
    }
  }

  return {
    ok: true,
    reason: `${remaining.toFixed(1)} m² headroom remains after this ${cat}.`,
    remainingFreeSqM: remaining,
    citation: "ANSI-A117.1-304",
    ...(soft !== undefined ? { soft } : {}),
  };
}
